import { useEffect } from "react";
import { useNavigate } from "react-router";

import {
  type UiCapabilities,
  type UiCapabilityKey,
  useUiCapabilities,
} from "@vega-api/extended/auth/useUiCapabilities";

export enum CapabilityLogic {
  AND = "AND",
  OR = "OR",
}

export type UseCapabilitiesGuardResult = {
  isLoading: boolean;
  hasCapabilities: boolean;
};

export type CapabilityCheckConfig = {
  capabilities: UiCapabilityKey[];
  logic?: CapabilityLogic;
};

export const useCapabilitiesGuard = (
  capabilityConfig: CapabilityCheckConfig | UiCapabilityKey,
  redirectPath?: string,
): UseCapabilitiesGuardResult => {
  const { capabilities, isLoading } = useUiCapabilities();
  const navigate = useNavigate();

  const config: CapabilityCheckConfig =
    typeof capabilityConfig === "string"
      ? { capabilities: [capabilityConfig], logic: CapabilityLogic.OR }
      : { logic: CapabilityLogic.OR, ...capabilityConfig };

  const { capabilities: requiredCapabilities, logic = CapabilityLogic.OR } =
    config;

  const hasCapabilities =
    !isLoading && checkCapabilities(requiredCapabilities, logic, capabilities);

  useEffect(() => {
    if (redirectPath && !isLoading && !hasCapabilities) {
      navigate(redirectPath, { replace: true });
    }
  }, [isLoading, hasCapabilities, navigate, redirectPath]);

  return {
    isLoading,
    hasCapabilities,
  };
};

const checkCapabilities = (
  requiredCapabilities: UiCapabilityKey[],
  logic: CapabilityLogic,
  capabilities: UiCapabilities,
): boolean => {
  if (logic === CapabilityLogic.AND) {
    return requiredCapabilities.every((capability) => capabilities[capability]);
  }

  return requiredCapabilities.some((capability) => capabilities[capability]);
};
